import { is, pipe, lensPath, ifElse, identity, always, partialRight, test } from 'ramda';

import * as RA from 'ramda-adjunct';
import { isValid, parse, format } from 'date-fns';

const iString = is(String);

const dateOrEmptyString = date => {
  if (isValid(new Date(date))) {
    return date;
  } else {
    return '';
  }
}

export const createTravel = ({
  travelId = '',
  type = '',
  dateOrigin = null,
  timeOrigin = null,
  dateArrival = null,
  timeArrival = null,
  origin = '',
  originCode = '',
  arrival = '',
  arrivalCode = '',
  carrier = '',
  travelNo = '',
  notes = '',
  travelCosts = '',
  travelAgent = '',
  staffId = '',
  date = null,
  contacts = [],
  fileKeys = [],
  eTickets = [],
  afterGigFlag = '0',
  calendar_date_id = undefined,
  calendar_date_id_out = undefined,
} = {}) => {

  date = dateOrEmptyString(date);
  dateOrigin = dateOrEmptyString(dateOrigin);
  dateArrival = dateOrEmptyString(dateArrival);

  return {
    travelId,
    type,
    dateOrigin,
    timeOrigin,
    dateArrival,
    timeArrival,
    origin,
    originCode,
    arrival,
    arrivalCode,
    carrier,
    travelNo,
    notes,
    travelCosts,
    travelAgent,
    staffId,
    date,
    contacts,
    fileKeys,
    eTickets,
    afterGigFlag,
    calendar_date_id,
    calendar_date_id_out,
  }
}


export const renameTravelKeysObject = RA.renameKeys({
  carrier: 'carrier',
  city1: 'origin',
  city2: 'arrival',
  code1: 'originCode',
  code2: 'arrivalCode',
  costs: 'travelCosts',
  date1: 'dateOrigin',
  date2: 'dateArrival',
  notes: 'notes',
  staff_id: 'staffId',
  time1: 'timeOrigin',
  time2: 'timeArrival',
  travel_agent: 'travelAgent',
  travel_booking_date: 'date',
  travel_id: 'travelId',
  travel_no: 'travelNo',
  travel_type: 'type',
});

export const createTravelFromResponse = pipe(
  renameTravelKeysObject,
  createTravel
)

export const travelsLens = lensPath(['travelDetailsState', 'data', 'travels']);
export const travelsLensFromState = lensPath(['data', 'travels']);

export const travelsOrEmpty = RA.viewOr([], travelsLens);

export const filterTravelBefore = (travels = [], cdate) => travels.filter(travel => travel.calendar_date_id == cdate.calendar_date_id).map(e => ({
  ...e,
  type: 'before'
}));
export const filterTravelAfter = (travels = [], cdate) => travels.filter(travel => travel.calendar_date_id_out == cdate.calendar_date_id).map(e => ({
  ...e,
  type: 'after'
}));

export const isTravelBeforeOnly = (travel) => travel.type == 'before' && !isTravelBeforeOut(travel);
export const isTravelBeforeOut = (travel) => travel.type == 'before' && travel.calendar_date_id_out;
export const isTravelAfterOnly = (travel) => travel.type == 'after' && !isTravelAfterOut(travel);
export const isTravelAfterOut = (travel) => travel.type == 'after' && travel.calendar_date_id;



export const contactRelationsOrEmpty = RA.viewOr([], lensPath(['travelDetailsState', 'data', 'contact_relations']));


const getDateObjFromDate = date => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate()
});

const getDateTimeObjFromDate = date => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
  hours: date.getHours(),
  minutes: date.getMinutes()
});

const getDateMust = ifElse(isValid, identity, always(new Date()))


export const trace = (v) => {
  console.log(v);
  return v;
}

/**
 * @description Parses the date string first. 
 * Then get a vaid date object, if invalid - get today,
 * Get Date object form valid date.
 */
export const stringToDateObj = pipe(
  parse,
  getDateMust,
  getDateObjFromDate
);
export const stringToDateTimeObj = pipe(
  parse,
  getDateMust,
  getDateTimeObjFromDate
);

export const getOnlyDateFromString = pipe(
  parse,
  getDateMust,
  partialRight(format, ['YYYY-MM-DD'])
);

export const getOnlyTimeFromString = pipe(
  parse,
  getDateMust,
  partialRight(format, ['HH:mm'])
)

// console.log(getOnlyTimeFromString(new Date()));


const testAirCode = test(/^\w{2}$/);
const testFlightCode = test(/^[ 0-9]{1,4}.*$/);
/**
 * @link https://en.wikipedia.org/wiki/Flight_number
 */
export const stringToFlightObj = flightString => {
  let air_code = '', flight_code = '';
  const flight_no = flightString.replace(/\s+/g, '');
  if(/^(([A-Za-z]{2,3})|([A-Za-z]\d)|(\d[A-Za-z]))(\d{1,4})([A-Za-z]?)$/i.test(flight_no)) {
      let air_codes = flight_no.match(/^(([A-Za-z]{2,3})|([A-Za-z]\d)|(\d[A-Za-z]))/i);
      air_code = air_codes[0] || "";
      flight_code = flight_no.replace(air_code, "");        
  }

  if (!(flight_code && air_code)) {
    throw Error('INVALID_FLIGHT_NUMBER');
  }
  return {
    airCode: air_code,
    flightCode: flight_code
  }
}

// console.log(testAirCode('LH'));
// console.log(testFlightCode('1041'));

// console.log(stringToFlightObj('LH104221'))


export const processFlightResponse = (res, selectedFlight) => {

  const scheduledFlight = res.data.content.scheduledFlights.find(flight => flight.referenceCode == selectedFlight.value);
  const airports = res.data.content.appendix.airports;
  const airlines = res.data.content.appendix.airlines;

  const originAirport = airports.find(e => e.fs === scheduledFlight.departureAirportFsCode);
  const originCity = originAirport.city;
  const arrivalAirport = airports.find(e => e.fs === scheduledFlight.arrivalAirportFsCode);
  const arrivalCity = arrivalAirport.city;

  const arrivalTerminal = scheduledFlight.arrivalTerminal;
  const originTerminal = scheduledFlight.departureTerminal;

  const carrierFsCode = scheduledFlight.carrierFsCode;
  const flightNo = scheduledFlight.flightNumber;

  const carrier = airlines.find(e => e.fs === carrierFsCode);

  return {
    // dateOrigin: getOnlyDateFromString(scheduledFlight.departureTime),
    timeOrigin: getOnlyTimeFromString(scheduledFlight.departureTime),
    // dateArrival: getOnlyDateFromString(scheduledFlight.arrivalTime),
    timeArrival: getOnlyTimeFromString(scheduledFlight.arrivalTime),
    originCode: scheduledFlight.departureAirportFsCode,
    arrivalCode: scheduledFlight.arrivalAirportFsCode,
    origin: originTerminal != undefined ? `${originCity} - Terminal ${originTerminal}` : originCity,
    arrival: arrivalTerminal != undefined ? `${arrivalCity} - Terminal ${arrivalTerminal}` : arrivalCity,
    carrier: carrier.name,
    travelNo: `${carrierFsCode}${flightNo}`,
  }

}







// const d = new Date('dw');
// const ds = '2018-12-16';


  // console.log(getDateObj(ds))

  // console.log(d.getDate())
  // console.log(d.getMonth()+1)
  // console.log((new Date()).getMinutes())

  // console.log(getDateMust(d))
